if (!customElements.get('product-shipping-announcement')) {
  customElements.define('product-shipping-announcement', class ProductShippingAnnouncement extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
        this.currentIndex = 0;
        this.rotationDelay = Math.max(3, Number(this.dataset.rotationSeconds) || 6) * 1000;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.querySelector('[data-announcement-previous]')?.addEventListener('click', () => this.show(this.currentIndex - 1, true));
        this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.currentIndex + 1, true));
        this.querySelector('[data-delivery-link]')?.addEventListener('click', (event) => this.goToDeliveryDetails(event));
        this.addEventListener('mouseenter', () => this.stopRotation());
        this.addEventListener('mouseleave', () => this.startRotation());
        this.addEventListener('focusin', () => this.stopRotation());
        this.addEventListener('focusout', () => this.startRotation());
        this.initialized = true;
      }

      this.renderDeliveryDate();
      this.show(0, false);
      this.startRotation();
    }

    renderDeliveryDate() {
      const metroTarget = this.querySelector('[data-dynamic-metro-delivery]');
      const nationalTarget = this.querySelector('[data-dynamic-national-delivery]');
      if (!metroTarget || !nationalTarget) return;

      const now = new Date();
      const cutoffHour = Number(this.dataset.cutoffHour) || 12;
      const cutoffMinute = Number(this.dataset.cutoffMinute) || 0;
      const nationalCutoffHour = Number(this.dataset.nationalCutoffHour) || 17;
      const nationalCutoffMinute = Number(this.dataset.nationalCutoffMinute) || 30;
      const minutesNow = now.getHours() * 60 + now.getMinutes();
      const metroDelivery = new Date(now);
      const nationalProcessing = new Date(now);
      const holidays = new Set((this.dataset.holidayDates || '')
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value)));

      if (this.isManualOverrideActive(now)) {
        const manualMetro = this.parseLocalDate(this.dataset.overrideMetroDate);
        const manualNational = this.parseLocalDate(this.dataset.overrideNationalDate);
        if (manualMetro && manualNational) {
          metroTarget.textContent = this.formatDeliveryDate(manualMetro, now, true);
          nationalTarget.textContent = this.formatDeliveryDate(manualNational, now, false);
          return;
        }
      }

      const closedToday = this.isClosedDay(now, holidays);
      const beforeCutoff = !closedToday && minutesNow < cutoffHour * 60 + cutoffMinute;
      const beforeNationalCutoff = !closedToday && minutesNow < nationalCutoffHour * 60 + nationalCutoffMinute;

      if (!beforeCutoff) metroDelivery.setDate(metroDelivery.getDate() + 1);
      this.moveToOpenDay(metroDelivery, holidays);

      if (!beforeNationalCutoff) nationalProcessing.setDate(nationalProcessing.getDate() + 1);
      this.moveToOpenDay(nationalProcessing, holidays);

      const nationalDelivery = new Date(nationalProcessing);
      nationalDelivery.setDate(nationalDelivery.getDate() + 1);
      this.moveToOpenDay(nationalDelivery, holidays);

      metroTarget.textContent = this.formatDeliveryDate(metroDelivery, now, true);
      nationalTarget.textContent = this.formatDeliveryDate(nationalDelivery, now, false);
    }

    dateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    parseLocalDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(year, month - 1, day, 12, 0, 0, 0);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    isClosedDay(date, holidays) {
      return date.getDay() === 0 || holidays.has(this.dateKey(date));
    }

    moveToOpenDay(date, holidays) {
      let guard = 0;
      while (this.isClosedDay(date, holidays) && guard < 31) {
        date.setDate(date.getDate() + 1);
        guard += 1;
      }
    }

    isManualOverrideActive(now) {
      if (this.dataset.overrideEnabled !== 'true') return false;
      const start = this.dataset.overrideStart;
      const end = this.dataset.overrideEnd;
      const today = this.dateKey(now);
      return /^\d{4}-\d{2}-\d{2}$/.test(start || '')
        && /^\d{4}-\d{2}-\d{2}$/.test(end || '')
        && today >= start
        && today <= end;
    }

    formatDeliveryDate(date, now, allowToday) {
      if (allowToday && date.toDateString() === now.toDateString()) return 'HOY*';
      const compact = window.matchMedia('(max-width: 749px)').matches;
      const formatted = new Intl.DateTimeFormat('es-SV', compact ? {
        weekday: 'long'
      } : {
        weekday: 'long', day: 'numeric', month: 'long'
      }).format(date).toLocaleUpperCase('es-SV');
      return `EL ${formatted}*`;
    }

    goToDeliveryDetails(event) {
      const target = document.querySelector('#ambienta-delivery-details');
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
      window.history.replaceState(null, '', '#ambienta-delivery-details');
    }

    show(index, restartRotation) {
      if (!this.slides.length) return;
      this.currentIndex = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      if (restartRotation) {
        this.stopRotation();
        this.startRotation();
      }
    }

    startRotation() {
      if (this.reduceMotion || this.slides.length < 2 || this.rotationTimer) return;
      this.rotationTimer = window.setInterval(() => this.show(this.currentIndex + 1, false), this.rotationDelay);
    }

    stopRotation() {
      window.clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }

    disconnectedCallback() {
      this.stopRotation();
    }
  });
}
